import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, ArrowRight, Loader2 } from "lucide-react";
import api from "../../../lib/api";
import { useAuth } from "../../../context/AuthContext";
import { PageTitle, Card, Loading, fmtDate } from "../ui";
import { StatusBadge, itemStatus, Modal, errText } from "./cmsShared";
import { useCmsBase } from "./cmsBase";

const GROUPS = [
  { s: "in_review", title: "Awaiting review" },
  { s: "changes_requested", title: "Changes requested" },
  { s: "draft", title: "Drafts" },
  { s: "published", title: "Published" },
  { s: "archived", title: "Unpublished" },
];
const SECTIONS = ["All", "Home", "History", "Freemasonry", "Visit", "News"];

function lastActor(i) {
  const d = i.draft;
  if (d?.reviewed_by_name && d.status === "changes_requested") return `Reviewed by ${d.reviewed_by_name}`;
  if (d?.submitted_by_name && d.status === "in_review") return `Submitted by ${d.submitted_by_name}`;
  if (d?.created_by_name) return `Draft by ${d.created_by_name}`;
  if (i.published?.published_by_name) return `Published by ${i.published.published_by_name}`;
  return "";
}

function NewArticleModal({ onClose }) {
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();
  const base = useCmsBase();
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/cms/articles", { title });
      nav(`${base}/content/${data.id}`);
    } catch (e) { setErr(errText(e)); setBusy(false); }
  };
  return (
    <Modal title="New article" onClose={onClose} testid="new-article-modal">
      <input autoFocus className="input-lodge" placeholder="Article title" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="new-article-title" />
      {err && <p className="mt-2 font-sans text-sm text-garnet">{err}</p>}
      <div className="mt-6 flex justify-end gap-3">
        <button onClick={onClose} className="btn-dark">Cancel</button>
        <button onClick={create} disabled={!title.trim() || busy} className="btn-sapphire" data-testid="new-article-create">
          {busy && <Loader2 size={15} className="animate-spin" />} Create draft
        </button>
      </div>
    </Modal>
  );
}

export default function ContentDashboard() {
  const { user } = useAuth();
  const base = useCmsBase();
  const [items, setItems] = useState(null);
  const [section, setSection] = useState("All");
  const [creating, setCreating] = useState(false);

  useEffect(() => { api.get("/cms/content").then(({ data }) => setItems(data)); }, []);

  const filtered = useMemo(() => (items || []).filter((i) => section === "All" || i.section === section), [items, section]);
  if (!items) return <Loading />;

  return (
    <div data-testid="cms-dashboard">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageTitle eyebrow="Content · Editorial" title="Public Website Content" sub="Draft, review and publish what visitors see on the Lodge website." />
        {user?.cms?.author && (
          <button onClick={() => setCreating(true)} className="btn-sapphire" data-testid="cms-new-article-btn"><Plus size={16} /> New article</button>
        )}
      </div>

      <div className="mb-8 flex flex-wrap gap-2">
        {SECTIONS.map((s) => (
          <button key={s} onClick={() => setSection(s)}
            className={`rounded-full px-4 py-1.5 font-sans text-xs font-semibold uppercase tracking-widest transition-colors ${
              section === s ? "bg-gold text-sapphire-deep" : "bg-white text-sapphire-deep/70 ring-1 ring-sapphire-deep/10 hover:text-gold-deep"
            }`}
            data-testid={`cms-filter-${s.toLowerCase()}`}>{s}</button>
        ))}
      </div>

      <div className="space-y-10">
        {GROUPS.map((g) => {
          const rows = filtered.filter((i) => itemStatus(i) === g.s);
          if (!rows.length) return null;
          return (
            <section key={g.s} data-testid={`cms-group-${g.s}`}>
              <h2 className="mb-3 flex items-center gap-3 font-display text-2xl font-semibold text-sapphire-deep">
                {g.title} <span className="font-sans text-sm font-normal text-stonegrey">{rows.length}</span>
              </h2>
              <Card className="!p-0">
                {rows.map((i, idx) => (
                  <Link key={i.id} to={`${base}/content/${i.id}`}
                    className={`group flex flex-wrap items-center gap-4 px-6 py-4 transition-colors hover:bg-parchment/60 ${idx ? "border-t border-sapphire-deep/5" : ""}`}
                    data-testid={`cms-row-${i.key}`}>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-display text-lg font-semibold text-sapphire-deep">{i.label}</p>
                      <p className="font-sans text-xs text-stonegrey">{i.section} · {lastActor(i)} · {fmtDate(i.updated_at)}</p>
                    </div>
                    {i.published && i.draft && <span className="font-sans text-[11px] italic text-emerald">Live version kept</span>}
                    <StatusBadge status={itemStatus(i)} />
                    <ArrowRight size={16} className="text-gold-deep transition-transform group-hover:translate-x-1" />
                  </Link>
                ))}
              </Card>
            </section>
          );
        })}
        {!filtered.length && <p className="font-sans text-sm text-stonegrey">No content in this section.</p>}
      </div>
      {creating && <NewArticleModal onClose={() => setCreating(false)} />}
    </div>
  );
}
